import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import Button, { buttonClass } from '../components/ui/Button';

type EmbedResponse =
  | { status: 'ok'; embedUrl?: string | null }
  | { status: 'activation_required'; invitationUrl: string; message: string }
  | { status: 'activation_pending'; message: string };

type State =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'empty'; message: string }
  | { status: 'ok'; embedUrl: string }
  | Exclude<EmbedResponse, { status: 'ok' }>;

const dashboardState = (response: EmbedResponse): State => {
  if (response.status !== 'ok') return response;
  const embedUrl = response.embedUrl?.trim();
  return embedUrl
    ? { status: 'ok', embedUrl }
    : { status: 'empty', message: 'No dashboard is available' };
};

const DashboardsPage: React.FC = () => {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [resending, setResending] = useState(false);

  const fetchEmbed = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const res = await axiosInstance.post<EmbedResponse>(
        `${config.accountsApiUrl}/quicksight/embed-url`,
        { allowedDomains: [window.location.origin] }
      );
      setState(dashboardState(res.data));
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load dashboard';
      setState({ status: 'error', error: msg });
    }
  }, []);

  const resendInvitation = useCallback(async () => {
    setResending(true);
    try {
      const res = await axiosInstance.post<EmbedResponse>(
        `${config.accountsApiUrl}/quicksight/resend-invitation`
      );
      setState(dashboardState(res.data));
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to resend invitation';
      setState({ status: 'error', error: msg });
    } finally {
      setResending(false);
    }
  }, []);

  useEffect(() => { fetchEmbed(); }, [fetchEmbed]);

  if (state.status === 'loading') {
    return <CenteredMessage>Loading dashboard…</CenteredMessage>;
  }

  if (state.status === 'error') {
    return (
      <CenteredMessage>
        <h1 className="mb-3 text-2xl font-bold">Dashboards</h1>
        <p className="text-status-error-foreground" role="alert">{state.error}</p>
        <Button variant="primary" className="mt-3" onClick={fetchEmbed} title="Ask for the dashboard again">
          Retry
        </Button>
      </CenteredMessage>
    );
  }

  if (state.status === 'empty') {
    return (
      <CenteredMessage>
        <h1 className="mb-3 text-2xl font-bold">Dashboards</h1>
        <p>{state.message}</p>
        <Button variant="primary" className="mt-3" onClick={fetchEmbed} title="Check again for a dashboard">Check again</Button>
      </CenteredMessage>
    );
  }

  if (state.status === 'activation_required') {
    return (
      <CenteredMessage>
        <h2 className="text-xl font-semibold mb-2">One-time setup</h2>
        <p className="mb-4 max-w-md text-center">{state.message}</p>
        {state.invitationUrl ? (
          <a
            href={state.invitationUrl}
            target="_blank"
            rel="noreferrer"
            className={buttonClass('primary')}
            title="Open the one-time QuickSight invitation in a new tab to set up your dashboard access"
          >
            Set up dashboard access
          </a>
        ) : (
          <p className="text-gray-600">Activation URL not available. Try Resend below.</p>
        )}
        <Button
          variant="link"
          size="md"
          className="mt-4"
          onClick={fetchEmbed}
          title="Once the invitation tab says setup is done, load the dashboard here"
        >
          I've completed setup — reload
        </Button>
      </CenteredMessage>
    );
  }

  if (state.status === 'activation_pending') {
    return (
      <CenteredMessage>
        <h2 className="text-xl font-semibold mb-2">Activation pending</h2>
        <p className="mb-4 max-w-md text-center">{state.message}</p>
        <div className="flex gap-3">
          <Button variant="primary" onClick={fetchEmbed} title="Check again whether your dashboard access is active and load it if so">
            Reload
          </Button>
          <Button
            variant="secondary"
            onClick={resendInvitation}
            disabled={resending}
            title="Get a fresh QuickSight setup invitation, for when the first one was closed before setup finished"
          >
            {resending ? 'Resending…' : 'Resend invitation'}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground max-w-sm text-center">
          Use Resend if you closed the original invitation tab without completing setup.
        </p>
      </CenteredMessage>
    );
  }

  return (
    <main className="flex h-screen min-h-0 flex-col">
      <h1 className="px-4 py-3 text-2xl font-bold">Dashboards</h1>
      <iframe
        src={state.embedUrl}
        title="FluxHive Dashboards"
        className="block min-h-0 w-full flex-1 border-0"
        allow="fullscreen"
      />
    </main>
  );
};

const CenteredMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col items-center justify-center h-screen p-6">
    {children}
  </div>
);

export default DashboardsPage;
